import React, { useState } from 'react';
import { getOrderStatusData } from '../../../../utils/helpers';
import type { CartItem, Order as OrderItem, AddressContainer, AddressStruct, OrderReceiptsResponse, YooKassaReceiptRecord } from '../../../../types/common';
import { updateOrderDetails } from '../../../../store/CommonSlice';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../../../store/store';
import classes from './OrderEditorPopup.module.css';

interface OrderEditorProps {
    orderData: {
        id: string;
        user: OrderItem['user'];
        orderNumber: string;
        orderStatus: string;
        timestamp: OrderItem['timestamp'];
        totalPrice: number;
        cart: CartItem[];
        totalDiscount: number;
        totalWeight: number;
        orderComment: string;
    };
    onClose: () => void;
}

const EMPTY_STRUCT: AddressStruct = {
    postalCode: '',
    region: '',
    city: '',
    cityType: '',
    dpdCityId: null,
    street: '',
    streetType: '',
    streetId: null,
    house: '',
    block: '',
    flat: '',
};

const OrderEditorPopup: React.FC<OrderEditorProps> = ({ orderData, onClose }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { name: displayStatus, style: statusStyle } = getOrderStatusData(orderData.orderStatus);
    const initialAddressContainer: AddressContainer | null = orderData.user.address;
    const initialAddressRaw = initialAddressContainer ? initialAddressContainer.raw : '';
    const [editedAddressRaw, setEditedAddressRaw] = useState<string>(initialAddressRaw);
    const [editedComment, setEditedComment] = useState(orderData.orderComment);
    const [receiptsOpen, setReceiptsOpen] = useState(false);
    const [receiptsLoading, setReceiptsLoading] = useState(false);
    const [receiptsError, setReceiptsError] = useState<string | null>(null);
    const [receiptsData, setReceiptsData] = useState<OrderReceiptsResponse | null>(null);
    const addressChanged = editedAddressRaw !== initialAddressRaw;
    const commentChanged = editedComment !== orderData.orderComment;
    const isChanged = addressChanged || commentChanged;

    const formatReceiptWhen = (receipt: YooKassaReceiptRecord) => {
        const raw = (receipt?.registered_at as string | null) || (receipt?.created_at as string | null) || null;
        return raw ? new Date(raw).toLocaleString('ru-RU') : '—';
    };

    const loadReceipts = async (force = false) => {
        if (!orderData.id) {
            setReceiptsError('Не удалось определить заказ для загрузки чеков');
            return;
        }
        if (receiptsLoading) return;
        if (receiptsData && !force) return;
        try {
            setReceiptsLoading(true);
            setReceiptsError(null);
            const base = String(process.env.REACT_APP_SERVER_URL || '').replace(/\/+$/, '');
            const r = await fetch(`${base}/orders/${encodeURIComponent(orderData.id)}/receipts`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
            setReceiptsData(data as OrderReceiptsResponse);
        } catch (error: any) {
            setReceiptsError(error?.message || 'Не удалось получить чеки заказа');
        } finally {
            setReceiptsLoading(false);
        }
    };

    const toggleReceipts = async () => {
        const next = !receiptsOpen;
        setReceiptsOpen(next);
        if (next && !receiptsData) {
            await loadReceipts();
        }
    };

    const handleSave = async () => {
        if (!isChanged) {
            onClose();
            return;
        }
        let nextAddressContainer: AddressContainer | null = initialAddressContainer || null;
        if (addressChanged) {
            const trimmed = editedAddressRaw.trim();
            if (!trimmed) {
                nextAddressContainer = null;
            } else if (nextAddressContainer) {
                nextAddressContainer = { ...nextAddressContainer, raw: trimmed };
            } else {
                nextAddressContainer = { raw: trimmed, struct: { ...EMPTY_STRUCT } };
            }
        }
        const payload: {
            id: string;
            address?: AddressContainer | null;
            orderComment?: string;
        } = { id: orderData.id };
        if (addressChanged) payload.address = nextAddressContainer;
        if (commentChanged) payload.orderComment = editedComment;
        try {
            await dispatch(updateOrderDetails(payload)).unwrap();
            console.log('Изменения сохранены:', payload);
        } catch (error) {
            console.error('Ошибка при сохранении изменений:', error);
        }
        onClose();
    };

    return (
        <div className={classes['orderEditorPopup']}>
            <div className={classes['orderEditorPopup__content']}>
                <div className={classes['orderEditorPopup__content_header']}>
                    <h2 className={classes['orderEditorPopup__content_header_title']}>Редактирование заказа {orderData.orderNumber}</h2>
                    <span style={statusStyle} className={classes['orderEditorPopup__content_header_status']}>
                        {displayStatus}
                    </span>
                </div>

                <div className={classes['orderEditorPopup__content_box']}>
                    <div className={classes['orderEditorPopup__content_box_field']}>
                        <label className={classes['orderEditorPopup__content_box_title']}>Адрес доставки:</label>
                        <textarea
                            className={classes['orderEditorPopup__content_box_textarea']}
                            rows={4}
                            value={editedAddressRaw}
                            onChange={(e) => setEditedAddressRaw(e.target.value)}
                        ></textarea>
                    </div>
                    <div className={classes['orderEditorPopup__content_box_field']}>
                        <label className={classes['orderEditorPopup__content_box_title']}>Комментарий к заказу:</label>
                        <textarea
                            className={classes['orderEditorPopup__content_box_textarea']}
                            rows={4}
                            value={editedComment}
                            onChange={(e) => setEditedComment(e.target.value)}
                        ></textarea>
                    </div>
                </div>

                <div style={{ marginTop: 16, display: 'grid', gap: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <strong>Чеки заказа</strong>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button className={classes['orderEditorPopup__content_buttons_elem']} type="button" onClick={() => void toggleReceipts()}>
                                {receiptsOpen ? 'Скрыть чеки' : 'Показать чеки'}
                            </button>
                            {/* {receiptsOpen ? (
                                <button
                                    className={classes['orderEditorPopup__content_buttons_elem']}
                                    type="button"
                                    onClick={() => void loadReceipts(true)}
                                    disabled={receiptsLoading}
                                >
                                    Обновить
                                </button>
                            ) : null} */}
                        </div>
                    </div>
                    {receiptsOpen ? (
                        <div
                            style={{
                                border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: 12,
                                padding: 12,
                                display: 'grid',
                                gap: 12,
                                maxHeight: 320,
                                overflow: 'auto',
                            }}
                        >
                            {receiptsLoading ? <div>Загрузка чеков…</div> : null}
                            {receiptsError ? <div style={{ color: '#ff8a8a' }}>{receiptsError}</div> : null}
                            {!receiptsLoading && !receiptsError && receiptsData ? (
                                <>
                                    {receiptsData.warnings?.length ? (
                                        <div style={{ color: '#ffca7a' }}>
                                            {receiptsData.warnings.map((warning, idx) => (
                                                <div key={`${warning}-${idx}`}>{warning}</div>
                                            ))}
                                        </div>
                                    ) : null}
                                    <div style={{ display: 'grid', gap: 8 }}>
                                        <strong>Чеки оплаты</strong>
                                        {receiptsData.paymentReceipts.length === 0 ? (
                                            <div>Чеки оплаты пока не найдены.</div>
                                        ) : (
                                            receiptsData.paymentReceipts.map((receipt, idx) => (
                                                <div
                                                    key={String(receipt?.id || `payment-receipt-${idx}`)}
                                                    style={{
                                                        border: '1px solid rgba(255,255,255,0.08)',
                                                        borderRadius: 10,
                                                        padding: '10px 12px',
                                                        display: 'grid',
                                                        gap: 6,
                                                    }}
                                                >
                                                    <div>
                                                        <strong>Чек оплаты #{idx + 1}</strong>
                                                    </div>
                                                    <div>ID: {String(receipt?.id || '—')}</div>
                                                    <div>
                                                        Тип: {String(receipt?.type || '—')} · Статус: {String(receipt?.status || '—')}
                                                    </div>
                                                    <div>Дата: {formatReceiptWhen(receipt)}</div>
                                                    <div>
                                                        ФД: {String(receipt?.fiscal_document_number ?? '—')} · ФП:{' '}
                                                        {String(receipt?.fiscal_document_attribute ?? '—')}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                    <div style={{ display: 'grid', gap: 8 }}>
                                        <strong>Возвраты и чеки возврата</strong>
                                        {receiptsData.refunds.length === 0 ? (
                                            <div>Возвратов по заказу пока нет.</div>
                                        ) : (
                                            receiptsData.refunds.map((refund, refundIdx) => (
                                                <div
                                                    key={refund.id}
                                                    style={{
                                                        border: '1px solid rgba(255,255,255,0.08)',
                                                        borderRadius: 10,
                                                        padding: '10px 12px',
                                                        display: 'grid',
                                                        gap: 8,
                                                    }}
                                                >
                                                    <div>
                                                        <strong>Возврат #{refundIdx + 1}</strong>
                                                    </div>
                                                    <div>
                                                        ID: {refund.id} · Статус: {String(refund.status || '—')} · Сумма:{' '}
                                                        {refund.amount?.value ? `${refund.amount.value} ${refund.amount.currency}` : '—'}
                                                    </div>
                                                    <div>Дата: {refund.createdAt ? new Date(refund.createdAt).toLocaleString('ru-RU') : '—'}</div>
                                                    {refund.warning ? <div style={{ color: '#ffca7a' }}>{refund.warning}</div> : null}
                                                    {refund.receipts.length === 0 ? (
                                                        <div>Чеки возврата пока не найдены.</div>
                                                    ) : (
                                                        refund.receipts.map((receipt, receiptIdx) => (
                                                            <div
                                                                key={String(receipt?.id || `${refund.id}-receipt-${receiptIdx}`)}
                                                                style={{
                                                                    border: '1px solid rgba(255,255,255,0.06)',
                                                                    borderRadius: 8,
                                                                    padding: '8px 10px',
                                                                    display: 'grid',
                                                                    gap: 6,
                                                                }}
                                                            >
                                                                <div>
                                                                    <strong>Чек возврата #{receiptIdx + 1}</strong>
                                                                </div>
                                                                <div>ID: {String(receipt?.id || '—')}</div>
                                                                <div>
                                                                    Тип: {String(receipt?.type || '—')} · Статус: {String(receipt?.status || '—')}
                                                                </div>
                                                                <div>Дата: {formatReceiptWhen(receipt)}</div>
                                                                <div>
                                                                    ФД: {String(receipt?.fiscal_document_number ?? '—')} · ФП:{' '}
                                                                    {String(receipt?.fiscal_document_attribute ?? '—')}
                                                                </div>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </>
                            ) : null}
                        </div>
                    ) : null}
                </div>

                <div className={classes['orderEditorPopup__content_buttons']}>
                    <button className={classes['orderEditorPopup__content_buttons_elem']} onClick={onClose}>
                        Отмена
                    </button>
                    <button className={classes['orderEditorPopup__content_buttons_elem']} disabled={!isChanged} onClick={handleSave}>
                        Сохранить
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OrderEditorPopup;
